import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    FormControl,
    Select,
    MenuItem,
    TextField,
    Chip,
    Popover,
    IconButton,
    InputAdornment,
    ToggleButton,
    ToggleButtonGroup,
    Collapse,
    Tooltip,
} from "@mui/material";
import { Filter, RotateCcw, Calendar as CalendarIcon, ChevronDown, X, Search } from "lucide-react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import axiosInstance from "../../../../../services/axiosInstance";
import apiEndPointsConfig from "../../../../../services/apiEndPoints";
import { DISPOSITION_OPTIONS, SUB_DISPOSITION_OPTIONS, RIDER_CLIENT_ID, RIDER_STATUS_OPTIONS } from "../utils/constants";
import { getDatePresetRange } from "../utils/helpers";

// ---- Shared look: every control is 36px high with the same border and radius ----
const CONTROL_HEIGHT = 36;
const controlBaseSx = {
    height: CONTROL_HEIGHT,
    borderRadius: "9px",
    fontSize: "0.8125rem",
    bgcolor: "#fff",
};

const selectSx = (active) => ({
    ...controlBaseSx,
    fontWeight: 600,
    "& .MuiSelect-select": { py: 0, pl: 1.5, pr: "34px !important", display: "flex", alignItems: "center" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: active ? "#C7D2FE" : "#E2E8F0" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: active ? "#A5B4FC" : "#CBD5E1" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6366F1", borderWidth: 1 },
    ...(active && { bgcolor: "#F5F7FF" }),
});

const menuItemSx = { fontSize: "0.8125rem", minHeight: 34 };

const toggleGroupSx = {
    bgcolor: "#fff",
    borderRadius: "9px",
    "& .MuiToggleButton-root": {
        height: CONTROL_HEIGHT,
        px: 1.5,
        textTransform: "none",
        fontSize: "0.8125rem",
        fontWeight: 600,
        color: "#475569",
        borderColor: "#E2E8F0",
        whiteSpace: "nowrap",
        "&:hover": { bgcolor: "#F8FAFC" },
        "&.Mui-selected": {
            bgcolor: "#EEF2FF",
            color: "#4338CA",
            borderColor: "#C7D2FE",
            "&:hover": { bgcolor: "#E0E7FF" },
        },
    },
    "& .MuiToggleButtonGroup-firstButton, & .MuiToggleButton-root:first-of-type": { borderTopLeftRadius: "9px", borderBottomLeftRadius: "9px" },
    "& .MuiToggleButtonGroup-lastButton, & .MuiToggleButton-root:last-of-type": { borderTopRightRadius: "9px", borderBottomRightRadius: "9px" },
};

const textButtonSx = {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.8rem",
    height: 34,
    px: 1.25,
    borderRadius: "9px",
    "&:hover": { bgcolor: "#F1F5F9" },
};

const PRIORITIES = [
    { value: "High", color: "#EF4444" },
    { value: "Medium", color: "#F59E0B" },
    { value: "Low", color: "#3B82F6" },
];

// Filters that live under "More filters"
const ADVANCED_KEYS = ["interest", "disposition", "subDisposition", "pocIdentified", "whatsappSent", "followUpStart"];

// Order-independent comparison of two filter objects
const sameFilters = (a, b) => {
    const keys = Object.keys(a);
    return keys.length === Object.keys(b).length && keys.every(k => a[k] === b[k]);
};

// Calendar day (YYYY-MM-DD) as a local Date — new Date("YYYY-MM-DD") is UTC
// midnight, which shows as the previous day west of UTC
const parseLocalDate = (day) => {
    if (!day) return null;
    const [y, m, d] = day.split("-").map(Number);
    return new Date(y, m - 1, d);
};

// The controls edit a draft; nothing is applied until "Filter" is pressed
// (or Enter in the search box). `appliedFilters` is what the pipeline shows.
const LeadFilters = ({ filters: appliedFilters, setFilters: applyFilters, userId, stages }) => {
    const [filters, setFilters] = useState(appliedFilters);
    const [managers, setManagers] = useState([]);
    const [calendarAnchor, setCalendarAnchor] = useState(null);
    const [followUpCalendarAnchor, setFollowUpCalendarAnchor] = useState(null);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [categories, setCategories] = useState([]);
    const userRole = localStorage.getItem("userRole");

    // Helper: YYYY-MM-DD in local time
    const formatLocalDate = (date) => {
        if (!date) return "";
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    // Follow the applied filters when they change elsewhere (restored on load,
    // pipeline switch, cleared)
    useEffect(() => {
        setFilters(appliedFilters);
    }, [appliedFilters]);

    const isDirty = !sameFilters(filters, appliedFilters);

    const handleApply = () => {
        if (isDirty) applyFilters(filters);
    };

    useEffect(() => {
        fetchManagers();
        fetchCategories();
    }, []);

    // Auto-show advanced row if any advanced filter is already set
    useEffect(() => {
        if (filters.interest || filters.disposition || filters.subDisposition || filters.whatsappSent || filters.followUpStart || filters.pocIdentified) {
            setShowAdvanced(true);
        }
    }, [filters]);

    const fetchManagers = async () => {
        try {
            const res = await axiosInstance.get(`${apiEndPointsConfig.getTeamMembersByClientId}/${userId}`);
            if (res.data.success) {
                setManagers(res.data.data.filter(m => m.role === "account manager"));
            }
        } catch (error) {
            console.error("Error fetching managers:", error);
        }
    };

    const fetchCategories = async () => {
        try {
            const res = await axiosInstance.get(`${apiEndPointsConfig.getCategories}/${userId}`);
            if (res.data.success) setCategories(res.data.data);
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    const handleChange = (field, value) => {
        setFilters(prev => {
            if ((prev[field] ?? "") === (value ?? "")) return prev;
            const next = { ...prev, [field]: value };
            // Remove empty values
            Object.keys(next).forEach(k => { if (next[k] === "" || next[k] === undefined) delete next[k]; });
            return next;
        });
    };

    const handleClear = () => {
        setFilters({});
        applyFilters({});
        setShowAdvanced(false);
    };

    const selectedDateRange = [
        parseLocalDate(filters.startDate),
        parseLocalDate(filters.endDate)
    ];

    const selectedFollowUpRange = [
        parseLocalDate(filters.followUpStart),
        parseLocalDate(filters.followUpEnd)
    ];

    const clearFollowUpDates = (e) => {
        e.stopPropagation();
        const next = { ...filters };
        delete next.followUpStart;
        delete next.followUpEnd;
        setFilters(next);
    };

    const clearDates = (e) => {
        e.stopPropagation();
        const next = { ...filters };
        delete next.startDate;
        delete next.endDate;
        delete next.datePreset;
        setFilters(next);
    };

    // Saved as a named preset so the filter still means "today" after a reload tomorrow
    const handleQuickDate = (type) => {
        setFilters({ ...filters, ...getDatePresetRange(type), datePreset: type });
    };

    const advancedCount = ADVANCED_KEYS.filter(k => appliedFilters[k]).length;

    const formatDay = (day) =>
        parseLocalDate(day).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    const formatRange = (start, end) =>
        start === end ? formatDay(start) : `${formatDay(start)} – ${formatDay(end)}`;

    const dateMode = filters.datePreset || (filters.startDate && filters.endDate ? "custom" : null);

    // Removing a chip from the "applied" row takes effect immediately
    const removeApplied = (keys) => {
        const next = { ...appliedFilters };
        keys.forEach(k => delete next[k]);
        applyFilters(next);
    };

    const stageName = (id) => stages.find(s => s._id === id)?.name || "Selected stage";
    const managerName = (id) => managers.find(m => m._id === id)?.name || "Selected member";

    // One chip per applied filter, in display order
    const appliedChips = [
        appliedFilters.search && { keys: ["search"], label: `Search: “${appliedFilters.search}”` },
        appliedFilters.startDate && appliedFilters.endDate && {
            keys: ["startDate", "endDate", "datePreset"],
            label: `Created: ${appliedFilters.datePreset === "today" ? "Today" : appliedFilters.datePreset === "yesterday" ? "Yesterday" : formatRange(appliedFilters.startDate, appliedFilters.endDate)}`,
        },
        appliedFilters.stage && { keys: ["stage"], label: `Stage: ${stageName(appliedFilters.stage)}` },
        appliedFilters.assignee && { keys: ["assignee"], label: `Assignee: ${appliedFilters.assignee === "unassigned" ? "Unassigned" : managerName(appliedFilters.assignee)}` },
        appliedFilters.priority && { keys: ["priority"], label: `Priority: ${appliedFilters.priority}` },
        appliedFilters.riderStatus && { keys: ["riderStatus"], label: `Status: ${appliedFilters.riderStatus}` },
        appliedFilters.interest && { keys: ["interest"], label: `Category: ${appliedFilters.interest}` },
        appliedFilters.disposition && { keys: ["disposition"], label: `Disposition: ${appliedFilters.disposition}` },
        appliedFilters.subDisposition && { keys: ["subDisposition"], label: `Sub disposition: ${appliedFilters.subDisposition}` },
        appliedFilters.pocIdentified && { keys: ["pocIdentified"], label: `POC: ${appliedFilters.pocIdentified === "yes" ? "Yes" : "No"}` },
        appliedFilters.whatsappSent && { keys: ["whatsappSent"], label: `WhatsApp: ${appliedFilters.whatsappSent === "true" ? "Sent" : "Not sent"}` },
        appliedFilters.followUpStart && appliedFilters.followUpEnd && {
            keys: ["followUpStart", "followUpEnd"],
            label: `Follow-up: ${formatRange(appliedFilters.followUpStart, appliedFilters.followUpEnd)}`,
        },
    ].filter(Boolean);

    // One per applied filter (a date range is one filter, not start + end)
    const activeCount = appliedChips.length;

    const renderSelect = ({ field, allLabel, options, minWidth = 150, label }) => (
        <FormControl size="small" sx={{ minWidth }}>
            <Select
                value={filters[field] || ""}
                onChange={(e) => handleChange(field, e.target.value)}
                displayEmpty
                IconComponent={(props) => <ChevronDown {...props} size={16} style={{ right: 10, color: "#94A3B8" }} />}
                renderValue={(value) => {
                    const option = options.find(o => o.value === value);
                    return (
                        <Box component="span" sx={{ display: "flex", gap: 0.5, alignItems: "center", overflow: "hidden" }}>
                            <Box component="span" sx={{ color: "#94A3B8", fontWeight: 500 }}>{label}:</Box>
                            <Box component="span" sx={{ color: value ? "#1E293B" : "#64748B", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {option ? option.label : allLabel}
                            </Box>
                        </Box>
                    );
                }}
                sx={selectSx(Boolean(filters[field]))}
                MenuProps={{ PaperProps: { sx: { borderRadius: "10px", mt: 0.5, boxShadow: "0 12px 24px -8px rgba(15,23,42,0.18)" } } }}
            >
                <MenuItem value="" sx={menuItemSx}>{allLabel}</MenuItem>
                {options.map(o => <MenuItem key={o.value} value={o.value} sx={menuItemSx}>{o.label}</MenuItem>)}
            </Select>
        </FormControl>
    );

    const calendarPopover = (anchor, setAnchor, value, onPick) => (
        <Popover
            open={Boolean(anchor)}
            anchorEl={anchor}
            onClose={() => setAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            PaperProps={{ sx: { mt: 1, borderRadius: "12px", boxShadow: "0 12px 28px -8px rgba(15,23,42,0.2)", border: "1px solid #EEF2F7" } }}
        >
            <Box sx={{ p: 1 }}>
                <Calendar
                    selectRange={true}
                    value={value[0] ? value : null}
                    onChange={(range) => {
                        if (range && range.length === 2) {
                            onPick(formatLocalDate(range[0]), formatLocalDate(range[1]));
                            setAnchor(null);
                        }
                    }}
                />
            </Box>
        </Popover>
    );

    return (
        <Box
            sx={{
                mb: 2,
                typography: "body2", // theme font for every label, incl. plain Box text
                bgcolor: "#fff",
                border: "1px solid #E8ECF2",
                borderRadius: "14px",
                boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                overflow: "hidden",
            }}
        >
            {/* ===== Header ===== */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.25, borderBottom: "1px solid #F1F4F8", flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: "#EEF2FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Filter size={15} color="#4F46E5" />
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", color: "#0F172A" }}>Filters</Typography>
                    {activeCount > 0 && (
                        <Box sx={{ px: 0.9, height: 20, borderRadius: 999, bgcolor: "#4F46E5", color: "#fff", fontSize: "0.7rem", fontWeight: 700, display: "flex", alignItems: "center" }}>
                            {activeCount} active
                        </Box>
                    )}
                    {isDirty && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, px: 1, height: 22, borderRadius: 999, bgcolor: "#FFFBEB", border: "1px solid #FDE68A", color: "#B45309", fontSize: "0.7rem", fontWeight: 600 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#F59E0B" }} />
                            Unapplied changes
                        </Box>
                    )}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
                    <Button
                        size="small"
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        endIcon={<ChevronDown size={14} style={{ transform: showAdvanced ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />}
                        sx={{ ...textButtonSx, color: showAdvanced || advancedCount ? "#4F46E5" : "#475569" }}
                    >
                        {showAdvanced ? "Fewer filters" : "More filters"}
                        {advancedCount > 0 && !showAdvanced && ` (${advancedCount})`}
                    </Button>
                    {(activeCount > 0 || isDirty) && (
                        <Button size="small" onClick={handleClear} startIcon={<RotateCcw size={13} />} sx={{ ...textButtonSx, color: "#DC2626", "&:hover": { bgcolor: "#FEF2F2" } }}>
                            Clear all
                        </Button>
                    )}
                    <Button
                        size="small"
                        variant="contained"
                        onClick={handleApply}
                        disabled={!isDirty}
                        startIcon={<Filter size={14} />}
                        sx={{
                            textTransform: "none", fontWeight: 700, fontSize: "0.8rem", height: 34, px: 2,
                            borderRadius: "9px", boxShadow: "none", bgcolor: "#4F46E5",
                            "&:hover": { bgcolor: "#4338CA", boxShadow: "0 4px 10px -2px rgba(79,70,229,0.4)" },
                            "&.Mui-disabled": { bgcolor: "#EEF0F4", color: "#A0A8B8" },
                        }}
                    >
                        Apply filters
                    </Button>
                </Box>
            </Box>

            {/* ===== Main filters ===== */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap", px: 2, py: 1.5 }}>
                <TextField
                    size="small"
                    placeholder="Search name, company or phone…"
                    value={filters.search || ""}
                    onChange={(e) => handleChange("search", e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleApply(); }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search size={15} color="#94A3B8" />
                            </InputAdornment>
                        ),
                        endAdornment: filters.search ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => handleChange("search", "")} sx={{ p: 0.25 }}>
                                    <X size={14} />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                    sx={{
                        flex: "1 1 240px",
                        maxWidth: 340,
                        "& .MuiInputBase-root": { ...controlBaseSx, pr: 1 },
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E8F0" },
                        "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
                        "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6366F1", borderWidth: 1 },
                        "& .MuiInputBase-input": { py: 0 },
                    }}
                />

                {/* Created date: Today | Yesterday | Custom range */}
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={dateMode}
                    onChange={(_, value) => {
                        if (value === "today" || value === "yesterday") handleQuickDate(value);
                    }}
                    sx={toggleGroupSx}
                >
                    <ToggleButton value="today">Today</ToggleButton>
                    <ToggleButton value="yesterday">Yesterday</ToggleButton>
                    <ToggleButton value="custom" onClick={(e) => setCalendarAnchor(e.currentTarget)}>
                        <CalendarIcon size={14} style={{ marginRight: 6 }} />
                        {dateMode === "custom" ? formatRange(filters.startDate, filters.endDate) : "Custom range"}
                    </ToggleButton>
                </ToggleButtonGroup>
                {dateMode && (
                    <Tooltip title="Clear date">
                        <IconButton size="small" onClick={clearDates} sx={{ ml: -0.75, color: "#94A3B8", "&:hover": { color: "#DC2626" } }}>
                            <X size={14} />
                        </IconButton>
                    </Tooltip>
                )}
                {calendarPopover(calendarAnchor, setCalendarAnchor, selectedDateRange, (startDate, endDate) => {
                    // A hand-picked range is fixed dates, not a relative preset
                    const next = { ...filters, startDate, endDate };
                    delete next.datePreset;
                    setFilters(next);
                })}

                {renderSelect({
                    field: "stage",
                    label: "Stage",
                    allLabel: "All",
                    options: stages.map(s => ({ value: s._id, label: s.name })),
                })}

                {/* Assignee - hidden for Account Managers (they only see their own leads) */}
                {userRole !== "account manager" && renderSelect({
                    field: "assignee",
                    label: "Assignee",
                    allLabel: "All",
                    minWidth: 170,
                    options: [{ value: "unassigned", label: "Unassigned" }, ...managers.map(m => ({ value: m._id, label: m.name }))],
                })}

                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={filters.priority || null}
                    onChange={(_, value) => handleChange("priority", value || "")}
                    sx={toggleGroupSx}
                >
                    {PRIORITIES.map(p => (
                        <ToggleButton key={p.value} value={p.value}>
                            <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: p.color, mr: 0.75 }} />
                            <span>{p.value}</span>
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>

                {/* Rider status — rider client only (Justtawk / Gazmin) */}
                {userId === RIDER_CLIENT_ID && (
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={filters.riderStatus || null}
                        onChange={(_, value) => handleChange("riderStatus", value || "")}
                        sx={toggleGroupSx}
                    >
                        {RIDER_STATUS_OPTIONS.map(status => (
                            <ToggleButton key={status} value={status}>{status}</ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                )}
            </Box>

            {/* ===== More filters (client details) ===== */}
            <Collapse in={showAdvanced} unmountOnExit>
                <Box sx={{ px: 2, pb: 1.5 }}>
                    <Box sx={{ borderTop: "1px dashed #E2E8F0", pt: 1.5, display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
                        <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#64748B", letterSpacing: "0.06em", textTransform: "uppercase", mr: 0.5 }}>
                            Client details
                        </Typography>

                        {renderSelect({
                            field: "interest",
                            label: "Category",
                            allLabel: "All",
                            options: categories.map(c => ({ value: c.name, label: c.name })),
                        })}
                        {renderSelect({
                            field: "disposition",
                            label: "Disposition",
                            allLabel: "All",
                            minWidth: 170,
                            options: DISPOSITION_OPTIONS.map(o => ({ value: o, label: o })),
                        })}
                        {renderSelect({
                            field: "subDisposition",
                            label: "Sub disposition",
                            allLabel: "All",
                            minWidth: 190,
                            options: SUB_DISPOSITION_OPTIONS.map(o => ({ value: o, label: o })),
                        })}
                        {renderSelect({
                            field: "pocIdentified",
                            label: "POC",
                            allLabel: "All",
                            minWidth: 120,
                            options: [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }],
                        })}

                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={filters.whatsappSent || null}
                            onChange={(_, value) => handleChange("whatsappSent", value || "")}
                            sx={toggleGroupSx}
                        >
                            <ToggleButton value="true">WhatsApp sent</ToggleButton>
                            <ToggleButton value="false">Not sent</ToggleButton>
                        </ToggleButtonGroup>

                        <ToggleButtonGroup exclusive size="small" value={filters.followUpStart ? "range" : null} sx={toggleGroupSx}>
                            <ToggleButton value="range" onClick={(e) => setFollowUpCalendarAnchor(e.currentTarget)}>
                                <CalendarIcon size={14} style={{ marginRight: 6 }} />
                                {filters.followUpStart && filters.followUpEnd
                                    ? `Follow-up: ${formatRange(filters.followUpStart, filters.followUpEnd)}`
                                    : "Follow-up date"}
                            </ToggleButton>
                        </ToggleButtonGroup>
                        {filters.followUpStart && (
                            <Tooltip title="Clear follow-up date">
                                <IconButton size="small" onClick={clearFollowUpDates} sx={{ ml: -0.75, color: "#94A3B8", "&:hover": { color: "#DC2626" } }}>
                                    <X size={14} />
                                </IconButton>
                            </Tooltip>
                        )}
                        {calendarPopover(followUpCalendarAnchor, setFollowUpCalendarAnchor, selectedFollowUpRange, (followUpStart, followUpEnd) =>
                            setFilters({ ...filters, followUpStart, followUpEnd })
                        )}
                    </Box>
                </Box>
            </Collapse>

            {/* ===== Applied filters ===== */}
            {appliedChips.length > 0 && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", px: 2, py: 1, bgcolor: "#F8FAFC", borderTop: "1px solid #F1F4F8" }}>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748B", mr: 0.5 }}>Showing:</Typography>
                    {appliedChips.map(chip => (
                        <Chip
                            key={chip.keys[0]}
                            size="small"
                            label={chip.label}
                            onDelete={() => removeApplied(chip.keys)}
                            deleteIcon={<X size={12} />}
                            sx={{
                                height: 24, borderRadius: "7px", bgcolor: "#fff", border: "1px solid #E0E7FF",
                                color: "#3730A3", fontWeight: 600, fontSize: "0.72rem",
                                "& .MuiChip-deleteIcon": { color: "#818CF8", mr: 0.5, "&:hover": { color: "#4F46E5" } },
                            }}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default LeadFilters;
